import { PlatformRequestError, platformErrorMessage, platformGet } from '@/apps/ingestion/services/_platformClient';

interface ListEnvelope<T> {
  data?: T[];
  has_more?: boolean;
  next_cursor?: string | null;
}

/** 5,000 rows at limit=100 — well past any legitimate panel/shell need. */
const MAX_PAGES = 50;

/**
 * Fetch every page of a cursor-paginated Platform API list endpoint.
 *
 * Platform API caps `limit` at 100 per page; these customer-care reference
 * reads (users, clients) expect the whole set, so we walk `next_cursor` until
 * `has_more` is false.
 *
 * A hard `maxPages` ceiling guards the interactive request path against
 * pathological growth: a normal panel/shell read never needs this many pages,
 * so hitting the cap signals abuse or a missing server-side aggregation.
 */
export async function fetchAllPages<T>(
  path: string,
  query: Record<string, unknown>,
  errorFallback: string,
  opts?: { maxPages?: number }
): Promise<T[]> {
  const cap = opts?.maxPages ?? MAX_PAGES;
  const rows: T[] = [];
  let cursor: string | null | undefined;
  let pages = 0;
  do {
    const { data, error, response } = await platformGet(path, {
      params: { query: { ...query, limit: 100, ...(cursor ? { cursor } : {}) } },
    });
    if (error) throw new PlatformRequestError(platformErrorMessage(error, errorFallback), response?.status, error);
    const envelope = (data ?? {}) as ListEnvelope<T>;
    rows.push(...(envelope.data ?? []));
    cursor = envelope.has_more ? envelope.next_cursor : null;
    if (++pages >= cap && cursor) {
      console.warn(`[fetchAllPages] hit page cap (${cap}) for ${path}; truncating`);
      break;
    }
  } while (cursor);
  return rows;
}
